import Card from '../components/card/Card';
import theme from '../styles/theme';
import { useEffect, useState } from 'react';
import H from '../components/texts/h/H';
import { useLocation, useParams } from 'react-router-dom';
import { Toast, useToast } from '../components/toast/toast';
import { capitalize, hasData } from '../utils/Utils';
import { initialClientData } from '../utils/Constants';
import Step from '../components/Step/Step';
import { clientData } from '../types/interfaces';
import Resume from '../organisms/Instrumentation/Resume';
import Contracts from '../organisms/Instrumentation/Contracts';
import {
  getApplication,
  sendApplication,
} from '../services/ApplicationService';
import useRole from '../hooks/useRole';
import ClientLines from '../organisms/Instrumentation/ClientLines';
import useStatus from '../hooks/useStatus';
import useStepNavigation from '../hooks/useStepNavigation';
import { useSignersContext } from '../context/SignersContext';
import { resolveAmountFallback } from '../utils/instrumentationUtils';
import { addYears, todayIso } from '../utils/vigenciaUtils';
import { useFiscalIdConfig } from '../hooks/useFiscalIdConfig';
import { getApplicationStatusKey } from '../utils/applicationStatusUtils';

const InstrumentationSteps = () => {
  const fiscalId = useFiscalIdConfig();
  const { id, cuit } = useParams();
  const { state } = useLocation();
  const { canEditInstrumentation } = useRole();
  const status = state?.status;
  const statusCode = state?.statusCode;
  const applicationStatusKey = getApplicationStatusKey(
    statusCode ?? status ?? ''
  );
  const { isRejected, isApproved, isFinished } = useStatus({
    applicationStatus: applicationStatusKey,
  });
  const [currentStep, setCurrentStep] = useState(1);
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const [isClientDataLoaded, setIsClientDataLoaded] = useState<boolean>(false);
  const [clientData, setClientData] = useState<clientData>(initialClientData);
  // Elevated vigencia state (task 53632): lives in the page so the frame dates
  // survive step navigation — Resume unmounts when leaving step 1.
  const [vigencia, setVigencia] = useState<{ startDate: string; endDate: string }>({
    startDate: todayIso(),
    endDate: addYears(todayIso(), 2),
  });
  const { savedSigners } = useSignersContext();

  // Hook para manejar la navegación automática de pasos
  const canEditInstrumentationSteps = canEditInstrumentation;

  const {
    maxStepReached,
    isInitialized,
    updateMaxStepReached,
    navigateToStep,
    goToNextStep,
    goToPreviousStep,
  } = useStepNavigation({
    cuit,
    id,
    moduleName: 'instrumentation',
    totalSteps: canEditInstrumentationSteps ? 3 : 2,
    currentStep,
    setCurrentStep,
  });

  // Asegurar que inicie en el paso 1 si viene desde el listado con state
  useEffect(() => {
    if (state && isInitialized) {
      setCurrentStep(1);
    }
  }, [state, isInitialized]);

  useEffect(() => {
    (async () => {
      if (cuit && id) {
        await getApplication(cuit, id).then((response) => {
          if (hasData(response)) {
            // Inicializar domicilio legal con valores fiscales si no están presentes
            const basicDataWithLegalAddress = {
              ...response.data.basic_data,
              legal_company_address: response.data.basic_data.legal_company_address || response.data.basic_data.company_address || '',
              legal_city: response.data.basic_data.legal_city || response.data.basic_data.city || '',
              legal_state: response.data.basic_data.legal_state || response.data.basic_data.state || '',
            };
            
            setClientData({
              ...response.data,
              basic_data: basicDataWithLegalAddress,
              signers: response.data.signers || [],
              
              /* 
                TODO: FUTURE REQUIREMENT - Multiple Signer Types
                When backend supports arrays, add this transformation:
                
                // Transform signers from backend (signer_type_code) to frontend (signer_type_codes)
                const transformedSigners = response.data.signers?.map((signer: any) => ({
                  ...signer,
                  signer_type_codes: signer.signer_type_code ? [signer.signer_type_code] : ['001'],
                })) || [];
                
                And use: signers: transformedSigners,
              */
              guarantee_amount: resolveAmountFallback(
                response.data.guarantee_amount,
                response.data.approved_amount
              ),
              note_amount: resolveAmountFallback(
                response.data.note_amount,
                response.data.approved_amount
              ),
            });
          }
        });
      }
    })();
  }, [cuit, id]);

  const handleRetryGet = () => {
    (async () => {
      if (cuit && id) {
        await getApplication(cuit, id).then((response) => {
          if (hasData(response)) {
            // Inicializar domicilio legal con valores fiscales si no están presentes
            const basicDataWithLegalAddress = {
              ...response.data.basic_data,
              legal_company_address: response.data.basic_data.legal_company_address || response.data.basic_data.company_address || '',
              legal_city: response.data.basic_data.legal_city || response.data.basic_data.city || '',
              legal_state: response.data.basic_data.legal_state || response.data.basic_data.state || '',
            };
            
            setClientData({
              ...response.data,
              basic_data: basicDataWithLegalAddress,
              signers: response.data.signers || [],
              
              /* 
                TODO: FUTURE REQUIREMENT - Multiple Signer Types
                When backend supports arrays, add this transformation:
                
                // Transform signers from backend (signer_type_code) to frontend (signer_type_codes)
                const transformedSigners = response.data.signers?.map((signer: any) => ({
                  ...signer,
                  signer_type_codes: signer.signer_type_code ? [signer.signer_type_code] : ['001'],
                })) || [];
                
                And use: signers: transformedSigners,
              */
              guarantee_amount: resolveAmountFallback(
                response.data.guarantee_amount,
                response.data.approved_amount
              ),
              note_amount: resolveAmountFallback(
                response.data.note_amount,
                response.data.approved_amount
              ),
            });
          }
        });
      }
    })();
  };

  const steps = [
    {
      id: 1,
      value: 'Resumen y documentos',
      element: (
        <Resume
          onPreviousClick={() => goToPreviousStep()}
          isSaving={isSaving}
          onSave={(dataToAdd: any, shouldContinue?: boolean) =>
            handleSave(dataToAdd, shouldContinue)
          }
          clientData={clientData}
          setClientData={setClientData}
          onLoadClientData={(e: boolean) => setIsClientDataLoaded(e)}
          isClientDataLoaded={isClientDataLoaded}
          signers={savedSigners}
          showSgrApprovedDistribution={false}
          vigencia={vigencia}
          setVigencia={setVigencia}
          validateAmounts={!isRejected && !isApproved && !isFinished}
        />
      ),
    },
    {
      id: 2,
      value: 'Contratos',
      element: (
        <Contracts
          applicationId={id}
          onPreviousClick={() => goToPreviousStep()}
          onNextClick={() => {
            goToNextStep();
            updateMaxStepReached(currentStep + 1);
          }}
          clientData={clientData}
          cuit={cuit}
        />
      ),
    },
    ...(canEditInstrumentationSteps
      ? [
        {
          id: 3,
          value: 'Cliente & Líneas',
          element: (
            <ClientLines
              cuit={cuit}
              applicationId={id}
              clientData={clientData}
              lines={clientData.approved_products}
              onPreviousClick={() => goToPreviousStep()}
              handleRetryGet={handleRetryGet}
              vigencia={vigencia}
            />
          ),
        },
      ]
      : []),
  ];

  const changeStep = (step: number) => {
    if (currentStep > step) {
      navigateToStep(step);
    }
  };

  const handleSave = (dataToAdd: any, shouldContinue: boolean = true) => {
    setIsSaving(true);
    if (!isRejected && !isApproved && !isFinished) {
      try {
        const updatedClientData = {
          ...clientData,
          signers: clientData.signers, // Usar clientData.signers que se actualiza correctamente
          corporate_structure: dataToAdd.corporateStructure,
          note_amount: dataToAdd.note_amount,
          guarantee_amount: dataToAdd.guarantee_amount,
        };

        setClientData(updatedClientData);

        const cleanApplicationFiles = [...dataToAdd.application_files];
      
        // Normalize signers to ensure consistent structure
        // Normalizar signers antes de enviar - convertir de signer_type_codes (array) a signer_type_code (string)
      
        const normalizedSigners = updatedClientData.signers.map((signer: any) => {
          const normalizedSigner = {
            name: signer.name || '',
            cuit: signer.cuit || '',
            locality: signer.locality || '',
            address: signer.address || '',
            role: signer.role || '',
            email: signer.email || '',
            // Usar el signer_type_code correcto (no el array signer_type_codes)
            signer_type_code: signer.signer_type_code || ['001'],
            asset_file_path: signer.asset_file_path || ''
          };
        
        
          return normalizedSigner;
        });
      
        const payload = {
          products: updatedClientData.products,
          manual_total_amount: updatedClientData.manual_total_amount,
          requested_amount: updatedClientData.requested_amount,
          reason: updatedClientData.reason || '',
          review: updatedClientData.review || '',
          extra_comments: updatedClientData.extra_comments || '',
          has_valid_corporate_composition:
            updatedClientData.has_valid_corporate_composition,
          signers: normalizedSigners,
          corporate_structure: updatedClientData.corporate_structure,
          application_files: cleanApplicationFiles,
          note_amount: dataToAdd.note_amount,
          guarantee_amount: dataToAdd.guarantee_amount,
          category_type_id: updatedClientData.category_type_id,
        };
      

        const cuit = updatedClientData.cuit;
        const id = String(updatedClientData.id_application);

        if (id?.length && id?.length > 0) {
          sendApplication(cuit || '', payload, id || '')
            .then((response) => {
              const saveOk =
                response?.status === 200 && response?.success !== false;
              if (saveOk) {
                useToast.success({
                  title: 'Guardado con éxito',
                  id: 'save-toast',
                  toastId: 'save-success',
                  description: '',
                  closeButton: true,
                  autoClose: 5000,
                });
              
                // Recargar datos después del guardado exitoso
                // Mantener los valores de domicilio legal editados por el usuario (no se persisten en BD)
                const currentLegalAddress = updatedClientData.basic_data.legal_company_address;
                const currentLegalCity = updatedClientData.basic_data.legal_city;
                const currentLegalState = updatedClientData.basic_data.legal_state;
              
                if (cuit && id) {
                  getApplication(cuit, id).then((reloadResponse) => {
                    if (hasData(reloadResponse)) {
                      const backendSigners = reloadResponse.data.signers || [];                   
                      // Usar signers del backend directamente
                      const finalSigners = backendSigners.map((signer: any) => ({
                        ...signer,
                        signer_type_codes: signer.signer_type_code ? [signer.signer_type_code] : ['001'],
                      }));
                    
                      // Mantener los valores de domicilio legal editados o inicializar con fiscales
                      const basicDataWithLegalAddress = {
                        ...reloadResponse.data.basic_data,
                        legal_company_address: currentLegalAddress || reloadResponse.data.basic_data.legal_company_address || reloadResponse.data.basic_data.company_address || '',
                        legal_city: currentLegalCity || reloadResponse.data.basic_data.legal_city || reloadResponse.data.basic_data.city || '',
                        legal_state: currentLegalState || reloadResponse.data.basic_data.legal_state || reloadResponse.data.basic_data.state || '',
                      };
            
                      setClientData({
                        ...reloadResponse.data,
                        basic_data: basicDataWithLegalAddress,
                        signers: finalSigners,
                        guarantee_amount: resolveAmountFallback(
                          reloadResponse.data.guarantee_amount,
                          reloadResponse.data.approved_amount
                        ),
                        note_amount: resolveAmountFallback(
                          reloadResponse.data.note_amount,
                          reloadResponse.data.approved_amount
                        ),
                      });
                    }
                  });
                }
              
                // Only navigate to next step if shouldContinue is true
                if (shouldContinue) {
                  handleNextStep(response.status);
                } else {
                  setIsSaving(false);
                }
              } else {
                setIsSaving(false);
                useToast.error({
                  title: 'Error al guardar la aplicación',
                  id: 'save-toast',
                  description: response?.message || 'Error desconocido',
                  closeButton: true,
                  autoClose: 5000,
                });
              }
            })
            .catch((error) => {
              console.error('Error saving application:', error);
              console.error('Error response:', error.response?.data);
              useToast.error({
                title: 'Error al guardar la aplicación',
                id: 'save-toast',
                description: error.response?.data?.message || 'Error desconocido',
                closeButton: true,
                autoClose: 5000,
              });
              setIsSaving(false);
            });
        }
      } catch (error) {
        console.error('Error saving application:', error);
        useToast.error({
          title: 'Error al guardar la aplicación',
          id: 'save-toast',
          description: 'Error desconocido',
          closeButton: true,
          autoClose: 5000,
        });
        setIsSaving(false);
      }
    } else {
      if (shouldContinue) {
        goToNextStep();
        updateMaxStepReached(currentStep + 1);
      }
      setIsSaving(false);
    }
  };

  const handleNextStep = (status: number | undefined) => {
    if (status === 200) {
      useToast.success({
        title: 'Guardado con éxito',
        id: 'save-toast',
        toastId: 'save-success',
        description: '',
        closeButton: true,
        autoClose: 5000,
      });
    } else {
      useToast.error({
        title: 'Ocurrió un error al guardar la información.',
        id: 'save-toast',
        description: '',
        closeButton: true,
        autoClose: 5000,
      });
    }
    goToNextStep();
    updateMaxStepReached(currentStep + 1);
    setIsSaving(false);
  };

  return (
    <Card
      variant='neutral'
      modifier='inverted'
      spacing={'4'}
      height='-webkit-fill-available'
      width='85vw'
    >
      <Toast id='save-toast' />
      <Card display='flex' margin={{ left: '2' }}>
        <H withMargin={false} type='2' weight='bold' modifier='strong'>
          {clientData
            ? 'Solicitud de ' +
            capitalize(clientData.basic_data.company_name) +
            ' ( ' +
            fiscalId.format(clientData.cuit) +
            ' )'
            : ''}
        </H>
      </Card>
      <Card
        display='flex'
        justifyContent='center'
        margin={{ top: '4', bottom: '4' }}
        width='100%'
      >
        <div
          style={{
            width: 'min(600px, 88%)',
            maxWidth: 600,
            position: 'relative',
            paddingTop: 4,
            paddingBottom: 0,
          }}
        >
          <div
            aria-hidden
            style={{
              position: 'absolute',
              left: '10%',
              right: '10%',
              top: 20,
              height: 1,
              background: theme.colors.neutral.border.weaker,
              pointerEvents: 'none',
            }}
          />
          <div style={{ display: 'flex', width: '100%', gap: 0, position: 'relative' }}>
            {steps.map((step, index) => (
              <Step
                key={step.id}
                widthContainer='auto'
                widthCircle1='0'
                widthId='32px'
                heightId='32px'
                widthCircle2='0'
                step={step}
                stepsLength={steps.length}
                index={index}
                currentStep={currentStep}
                changeStep={(step) => changeStep(step)}
                atlanticStyle
              />
            ))}
          </div>
        </div>
      </Card>
      {steps[currentStep - 1]?.element}
    </Card>
  );
};

export default InstrumentationSteps;
